import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import saveAs from 'file-saver';
import { isSmmParameterIndex, SmmParameter } from 'motion-master-client';
import { concatAll, concatMap, filter, map } from 'rxjs/operators';
import { gte, lt } from 'semver';

import { assignOptionsToSmmParameters, updateDeviceParameters } from './device-parameter.actions';
import { DeviceParameter } from './device-parameter.model';
import {
  loadDeviceSmmParameters,
  modifySmmFirmwareUnsupportedParameters,
  saveSmmConfigurationToFile,
  updateSmmParameters,
  upsertSmmParameters,
} from './smm-parameter.actions';
import { smmLw2FirmwareUnsupportedParameters, smmLw2V1FirmwareUnsupportedParameters } from './smm-parameter.model';

import { ConnectionService } from '@app/services/connection.service';
import { DeviceService } from '@app/services/device.service';
import { loadDeviceSmmFirmwareVersion, updateDevice } from '@app/store/device.actions';
import { getCurrentValue } from '@app/util';

@Injectable()
export class SmmParameterEffects {
  private actions$ = inject(Actions);
  private cn = inject(ConnectionService);
  private deviceService = inject(DeviceService);

  // `loadDeviceSmmParameters` is dispatched in device.guard.ts, check if needed earlier
  handleLoadDeviceSmmParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDeviceSmmParameters),
      filter(({ device }) => getCurrentValue(this.deviceService.selectDeviceHasSmm(device))),
      concatMap(({ device }) =>
        this.cn.client.request.getSmmOdParameters(device.serialNumber, undefined, 'device').pipe(
          map(([versionParameter, ...smmParameters]) => [
            updateDevice({
              update: {
                changes: { smmParameterStructureVersion: versionParameter.value as number },
                id: device.serialNumber,
              },
            }),
            upsertSmmParameters({ smmParameters }),
            assignOptionsToSmmParameters({ device }),
            /**
             * Read device SMM firmware version to be able to display
             * the button for updating the SMM firmware on device
             * details page for devices that have SMM firmware v1.3.
             */
            loadDeviceSmmFirmwareVersion({ device }),
          ]),
          concatAll(),
        ),
      ),
    ),
  );

  assignOptionsToSmmParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(assignOptionsToSmmParameters),
      filter(({ device }) => getCurrentValue(this.deviceService.selectDeviceHasSmm(device))),
      map(({ device }) => {
        const smmParameters = getCurrentValue(this.deviceService.selectDeviceSmmParameters(device));
        const parameters = getCurrentValue(this.deviceService.selectDeviceParameters(device));

        const updates = parameters.map((parameter) => {
          let changes: Partial<DeviceParameter> = {};

          if (isSmmParameterIndex(parameter.index)) {
            const obj = smmParameters.find((p) => p.index === parameter.index && p.subindex === parameter.subindex);
            const options = obj?.options;
            if (options) {
              changes = { options, inputType: 'select' };
            }
          }

          return { changes, id: parameter.id };
        });
        return updateDeviceParameters({ device, updates });
      }),
    ),
  );

  handleSaveSmmConfigurationToFile$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(saveSmmConfigurationToFile),
        map(({ device }) => {
          const parameters = getCurrentValue(this.deviceService.selectDeviceSmmParameters(device));
          if (parameters) {
            let lines = parameters.map((p) => `0x${p.index.toString(16)}, ${p.subindex}, ${p.value}`);
            const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });

            const saveAsFilename = this.deviceService.prefixDeviceFilename(device, 'smm.csv');
            saveAs(blob, saveAsFilename);
          }
        }),
      ),
    { dispatch: false },
  );

  modifyFirmwareUnsupportedSmmParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(modifySmmFirmwareUnsupportedParameters),
      filter(({ device }) => getCurrentValue(this.deviceService.selectDeviceHasSmm(device))),
      map(({ device }) => {
        const smmFirmwareVersion = getCurrentValue(
          this.deviceService.selectDeviceProperty(device, 'smmFirmwareVersion'),
        );
        const parsedSmmFirmwareVersion = `${smmFirmwareVersion[1]}.${smmFirmwareVersion[0]}.0`;
        const smmParameters = getCurrentValue(this.deviceService.selectDeviceSmmParameters(device));
        let smmParameterUpdates: { changes: Partial<SmmParameter>; id: string }[] = [];

        // Make certain parameters read-only for SMM versions lower than v2.0
        if (lt(parsedSmmFirmwareVersion, '2.0.0')) {
          const disabledParameters = smmLw2V1FirmwareUnsupportedParameters
            .map(([index, subindex]) => smmParameters.find((p) => p.index === index && p.subindex === subindex))
            .filter(Boolean)
            .map((p) => ({
              changes: { writeAccess: false },
              id: p.id,
            }));

          smmParameterUpdates = [...smmParameterUpdates, ...disabledParameters];
        }

        // Hide SMM LW2 unsupported parameters
        if (gte(parsedSmmFirmwareVersion, '1.3.0')) {
          const unsupportedLW2Parameters = smmLw2FirmwareUnsupportedParameters
            .map(([index, subindex]) => smmParameters.find((p) => p.index === index && p.subindex === subindex))
            .filter(Boolean)
            .map((p) => ({
              changes: { hidden: true, writeAccess: false },
              id: p.id,
            }));

          smmParameterUpdates = [...smmParameterUpdates, ...unsupportedLW2Parameters];
        }

        return updateSmmParameters({ smmParameterUpdates });
      }),
    ),
  );
}
